/**
 * MUI `Menu` with multi-select checkboxes for string column values.
 * Supports optional search, select-all (respecting current search), clear, and apply.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Popover,
  Button,
  Divider,
  TextField,
  alpha,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { SEARCE_COLORS } from "../../theme";

export interface ColumnFilterProps {
  /** Element the menu anchors to */
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Called with the full selected set when the user clicks Apply */
  onSelect: (values: string[]) => void;
  options: string[];
  selectedValues: string[];
  /** Shown in the header as "Filter {title}" */
  title: string;
  /** When true, shows a search field and filters the option list */
  enableSearch?: boolean;
}

/**
 * Standalone column filter popover (simpler alternative to the unified column sort/filter menu).
 *
 * @param props - {@link ColumnFilterProps}
 * @returns MUI menu with checkbox list and apply/cancel actions
 */
export const ColumnFilter: React.FC<ColumnFilterProps> = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  options,
  selectedValues,
  title,
  enableSearch = false,
}) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selectedValues);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    setLocalSelected(selectedValues);
  }, [selectedValues]);

  const filteredOptions = enableSearch
    ? options.filter((option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  useEffect(() => {
    if (open) {
      setSearchTerm("");
    }
  }, [open]);

  const handleToggle = (value: string) => {
    const currentIndex = localSelected.indexOf(value);
    const newSelected = [...localSelected];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setLocalSelected(newSelected);
  };

  const handleApply = () => {
    onSelect(localSelected);
  };

  const handleClear = () => {
    setLocalSelected([]);
  };

  const handleSelectAll = () => {
    const optionsToSelect =
      enableSearch && searchTerm ? filteredOptions : options;
    setLocalSelected([...optionsToSelect]);
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          width: 300,
          maxHeight: 420,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* ---- Fixed header ---- */}
      <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
            Filter {title}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              onClick={handleSelectAll}
              sx={{ fontSize: "0.7rem", minWidth: "auto", px: 1 }}
            >
              All
            </Button>
            <Button
              size="small"
              onClick={handleClear}
              sx={{ fontSize: "0.7rem", minWidth: "auto", px: 1 }}
            >
              Clear
            </Button>
          </Box>
        </Box>

        <Divider />

        {enableSearch && (
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    mr: 1,
                    color: SEARCE_COLORS.textSecondary,
                    fontSize: "1rem",
                  }}
                />
              ),
            }}
            sx={{ my: 1.5 }}
          />
        )}
      </Box>

      {/* ---- Scrollable options ---- */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2 }}>
        {filteredOptions.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              py: 2,
              color: SEARCE_COLORS.textSecondary,
              fontSize: "0.75rem",
            }}
          >
            {enableSearch && searchTerm
              ? "No results found"
              : "No options available"}
          </Typography>
        ) : (
          filteredOptions.map((option) => (
            <Box
              key={option}
              sx={{
                display: "flex",
                alignItems: "center",
                py: 0.5,
                px: 1,
                cursor: "pointer",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                },
              }}
              onClick={() => handleToggle(option)}
            >
              <input
                type="checkbox"
                checked={localSelected.includes(option)}
                onChange={() => handleToggle(option)}
                style={{ marginRight: 8 }}
              />
              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                {option}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      {/* ---- Fixed footer ---- */}
      <Box sx={{ flexShrink: 0, px: 2, pb: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Button size="small" onClick={onClose} sx={{ fontSize: "0.7rem" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{
              fontSize: "0.7rem",
              backgroundColor: SEARCE_COLORS.primary,
              "&:hover": {
                backgroundColor: "#0056cc",
              },
            }}
          >
            Apply ({localSelected.length})
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

